import React from 'react';
import { Handle, Position } from 'reactflow';
import { useParams } from 'react-router-dom';
import {
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Database,
  AlertTriangle,
  ExternalLink,
  Square,
} from 'lucide-react';
import { NodeData, RegistryEnabledModel } from '../../../types';
import { useFlowStore } from '../../../store';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import apiService from '../../../services/api';
import { useContextualNavigation } from '../../../hooks/useContextualNavigation';
import { useEntityEvent } from '../../../hooks/useEntityEvent';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';

interface FilterNodeProps {
  data: NodeData;
  selected: boolean;
}

/**
 * Workflow node for the `filter` step type (AC-1273, AC-1278).
 *
 * Layout is mode-aware (AC-1371):
 *   - Mode sub-label ("PREDICATE" or "REGISTRY DEDUP") so users see at a
 *     glance which kind of filter this is.
 *   - Predicate mode: source -> output, first condition inline, last-run
 *     kept/input badge.
 *   - Registry-dedup mode: source -> output, a registry context block
 *     showing the resolved model class + dedup fields (clickable, opens
 *     the schemas page), Register-kept / Check-only pill, and the same
 *     last-run badge. Misconfigurations (unresolved registry, empty
 *     dedup_fields) surface as amber warnings inline so the author
 *     doesn't have to open the config dialog to spot them.
 */
const FilterNode: React.FC<FilterNodeProps> = ({ data, selected }) => {
  const { nodes, edges, setNodes, setEdges } = useFlowStore();
  const { navigateWithContext } = useContextualNavigation();
  const { projectId } = useParams<{ projectId: string }>();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // AC-1406: Delete via React Query mutation hook.
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');

  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      (n) =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id),
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id, data.step]);

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;
    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });
      setNodes(nodes.filter((node) => node.id !== data.id));
      setEdges(edges.filter((edge) => edge.source !== data.id && edge.target !== data.id));
    } catch (error) {
      console.error('Failed to delete filter step:', error);
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Clock className="w-4 h-4 text-primary-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/80';
      case 'completed':
        return 'border-green-500 bg-green-50 dark:bg-green-900/80';
      case 'failed':
        return 'border-red-500 bg-red-50 dark:bg-red-900/80';
      default:
        return 'border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-gray-800';
    }
  };

  const cfg: any = (data.step as any)?.config || {};
  const mode: 'predicate' | 'registry_dedup' = cfg.mode || 'predicate';
  const sourceField: string = cfg.source_field || '';
  const outputField: string = cfg.output_field || sourceField;
  const conditions: string[] = Array.isArray(cfg.conditions) ? cfg.conditions : [];
  const conditionsCount = conditions.length;
  const elementClassName: string | undefined = cfg.element_class_name;
  const explicitRegistryModelId: number | null =
    cfg.registry_model_id === undefined || cfg.registry_model_id === null
      ? null
      : Number(cfg.registry_model_id);
  // register_kept defaults to true on the backend; treat undefined as true so
  // legacy configs render the same badge they would after re-saving.
  const registerKept: boolean = cfg.register_kept !== false;
  // AC-1371: on_empty='exit' silently halts the workflow when nothing passes
  // the filter. The default ('continue') is invisible because it's expected,
  // but 'exit' is high-impact and worth a badge.
  const stopOnEmpty: boolean = cfg.on_empty === 'exit';

  // AC-1371: fetch registry-enabled models so we can resolve the model that
  // the backend's FilterStepExecutor will use, and surface its dedup fields.
  // Only fetched when needed; predicate mode never triggers the request.
  const [registryModels, setRegistryModels] = React.useState<RegistryEnabledModel[]>([]);
  const [registryModelsLoaded, setRegistryModelsLoaded] = React.useState(false);

  const fetchRegistryModels = React.useCallback(async () => {
    if (mode !== 'registry_dedup') return;
    if (!projectId) return;
    try {
      const numericProjectId = Number(projectId);
      if (!Number.isFinite(numericProjectId)) return;
      const models = await apiService.getRegistryEnabledModels(numericProjectId);
      setRegistryModels(models || []);
    } catch (err) {
      console.warn('FilterNode: failed to load registry-enabled models', err);
      setRegistryModels([]);
    } finally {
      setRegistryModelsLoaded(true);
    }
  }, [mode, projectId]);

  React.useEffect(() => {
    let cancelled = false;
    if (mode !== 'registry_dedup') {
      setRegistryModels([]);
      setRegistryModelsLoaded(false);
      return;
    }
    (async () => {
      if (!cancelled) await fetchRegistryModels();
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, fetchRegistryModels]);

  // AC-1369 + AC-1371: when a model's registry config changes (e.g. dedup
  // fields edited on the schema editor), refetch so the node reflects the
  // new shape without a full workflow reload.
  useEntityEvent({
    entity: 'model',
    handler: (event) => {
      if (event.kind === 'updated' || event.kind === 'deleted') {
        fetchRegistryModels();
      }
    },
  });

  // Resolve the registry model the backend will actually use. Mirrors
  // FilterStepExecutor._resolve_registry_model: explicit id wins, else look
  // up by upstream element class name.
  const resolvedRegistryModel: RegistryEnabledModel | null = React.useMemo(() => {
    if (mode !== 'registry_dedup') return null;
    if (explicitRegistryModelId !== null) {
      return registryModels.find((m) => m.model_id === explicitRegistryModelId) || null;
    }
    if (elementClassName) {
      return registryModels.find((m) => m.class_name === elementClassName) || null;
    }
    return null;
  }, [mode, explicitRegistryModelId, elementClassName, registryModels]);

  const dedupFields: string[] = resolvedRegistryModel?.registry_dedup_fields || [];

  // AC-1302: Parse the first condition string into (field, operator, value) so
  // the node body can render it inline matching the ConditionalNode pattern.
  // Condition strings are produced by ConditionBuilder / rewriter as
  // "<field> <op> <value>" where op is one of >= <= != == > <.
  const parseConditionExpression = (
    expr: string,
  ): { field: string; operator: string; value: string } | null => {
    if (!expr || typeof expr !== 'string') return null;
    const ops = [' >= ', ' <= ', ' != ', ' == ', ' > ', ' < '];
    for (const op of ops) {
      const idx = expr.indexOf(op);
      if (idx > 0) {
        return {
          field: expr.slice(0, idx).trim(),
          operator: op.trim(),
          value: expr.slice(idx + op.length).trim(),
        };
      }
    }
    return null;
  };

  const formatFieldPath = (field: string): string => {
    if (!field.includes('.')) return field;
    const parts = field.split('.');
    if (parts.length > 2) return `${parts[0]}...${parts[parts.length - 1]}`;
    return field;
  };

  const firstParsed = conditionsCount > 0 ? parseConditionExpression(conditions[0]) : null;

  // Last-run kept/input badge.
  const lastRun: any = (data.step as any)?.last_run_result?.final_output;
  const hasLastRun =
    lastRun &&
    typeof lastRun === 'object' &&
    typeof lastRun.input_count === 'number' &&
    typeof lastRun.kept_count === 'number';

  // Click handler for the registry chip - opens the schemas page focused on
  // the specific model so the user can inspect or change registry_dedup_fields
  // without having to scroll or search the list. Falls back to the schemas
  // list when we don't have a resolved model id.
  const handleOpenSchema = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!projectId) return;
    const path = resolvedRegistryModel
      ? `/projects/${projectId}/schemas?modelId=${resolvedRegistryModel.model_id}`
      : `/projects/${projectId}/schemas`;
    navigateWithContext(path, 'Workflow');
  };

  return (
    <div className="relative">
      <div
        className={`relative w-72 min-h-[8rem] px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {isDeletable && (
          <button
            type="button"
            title="Delete step"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
            className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Filter badge (rose palette). */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-rose-500 to-rose-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </div>
        </div>

        {/* Body - kept compact so the node matches the height of Create /
            Queue Write nodes; the mode is conveyed by content (registry chip
            vs conditions chip), not by a standalone label row. AC-1466:
            pt-3 (vs the original py-1) gives the first body chip clearance
            below the protruding -top-3 "Filter" pill so the metadata
            (source_field, condition chip) doesn't visually clip into it. */}
        <div className="flex flex-col items-center pt-3 pb-1 w-full">
          {getStatusIcon() && (
            <div className="flex items-center justify-center w-full mb-1">
              {getStatusIcon()}
            </div>
          )}

          {sourceField ? (
            <Tooltip content={`Filtering array field "${sourceField}" -> "${outputField}"`}>
              <div className="px-2 py-1 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200 text-xs border border-rose-200 dark:border-rose-700 mx-2 self-stretch mb-1 font-mono truncate">
                {sourceField}
                {outputField && outputField !== sourceField ? ` -> ${outputField}` : ''}
              </div>
            </Tooltip>
          ) : (
            <div className="text-xs italic text-gray-500 dark:text-gray-400 mb-1">No source field set</div>
          )}

          {/* AC-1302: inline conditions display (predicate mode only). */}
          {mode === 'predicate' && conditionsCount > 0 && firstParsed && (
            <Tooltip content={conditions[0]}>
              <div className="px-2 py-1 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200 text-[11px] border border-rose-200 dark:border-rose-700 mx-2 self-stretch mb-1 leading-tight truncate">
                <span className="font-mono text-blue-700 dark:text-blue-300">{formatFieldPath(firstParsed.field)}</span>
                <span className="text-gray-600 dark:text-gray-400 mx-1">{firstParsed.operator}</span>
                <span className="font-mono text-green-700 dark:text-green-300">{firstParsed.value}</span>
                {conditionsCount > 1 && (
                  <span className="text-rose-600 dark:text-rose-400 ml-1">+{conditionsCount - 1}</span>
                )}
              </div>
            </Tooltip>
          )}
          {mode === 'predicate' && conditionsCount > 0 && !firstParsed && (
            <div className="text-[11px] text-gray-700 dark:text-gray-300 mb-1" title={conditions[0]}>
              {conditionsCount} condition{conditionsCount === 1 ? '' : 's'}
            </div>
          )}

          {/* AC-1371: registry-dedup context - one compact chip combining
              the resolved class name and dedup field, click to schemas. */}
          {mode === 'registry_dedup' && registryModelsLoaded && (
            resolvedRegistryModel ? (
              <Tooltip content="Open the schemas page to inspect or edit registry dedup fields">
                <button
                  type="button"
                  onClick={handleOpenSchema}
                  className="group w-full text-left px-2 py-1 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-800 dark:text-rose-200 text-[11px] border border-rose-200 dark:border-rose-700 hover:bg-rose-100 dark:hover:bg-rose-900/50 transition-colors mx-2 self-stretch mb-1 flex items-center justify-between gap-1"
                  data-testid="filter-registry-model-chip"
                >
                  <span className="inline-flex items-center gap-1 font-mono align-middle min-w-0">
                    <Database className="w-3 h-3 flex-shrink-0" />
                    {dedupFields.length > 0 ? (
                      <span className="truncate">
                        {resolvedRegistryModel.class_name}.{dedupFields.join(', ')}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 truncate"
                        data-testid="filter-no-dedup-fields-warning"
                      >
                        <span className="truncate">{resolvedRegistryModel.class_name}</span>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span>whole object</span>
                      </span>
                    )}
                  </span>
                  {/* Click affordance - subtle until hover so it doesn't
                      compete with the chip content for attention. */}
                  <ExternalLink
                    className="w-3 h-3 flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity"
                    data-testid="filter-registry-external-link-icon"
                  />
                </button>
              </Tooltip>
            ) : (
              <div
                className="px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[11px] flex items-center gap-1 border border-amber-200 dark:border-amber-700 mx-2 self-stretch mb-1"
                data-testid="filter-registry-unresolved-warning"
              >
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                <span>Registry unresolved</span>
              </div>
            )
          )}

          {/* Bottom row of small inline pills: register-kept indicator (only
              meaningful in registry_dedup mode), stop-on-empty indicator
              (only when on_empty='exit', because the default 'continue' is
              expected), and last-run kept/input counter. Sharing one row
              avoids stacking three short pills. */}
          {(mode === 'registry_dedup' || stopOnEmpty || hasLastRun) && (
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              {mode === 'registry_dedup' && (
                registerKept ? (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 font-medium"
                    data-testid="filter-register-kept-badge"
                  >
                    Register kept
                  </span>
                ) : (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium"
                    data-testid="filter-check-only-badge"
                  >
                    Check only
                  </span>
                )
              )}
              {stopOnEmpty && (
                <Tooltip content="Workflow halts when no elements pass the filter (on_empty='exit')">
                  <span
                    className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-medium"
                    data-testid="filter-stop-on-empty-badge"
                  >
                    <Square className="w-2.5 h-2.5 flex-shrink-0" />
                    <span>Stop on empty</span>
                  </span>
                </Tooltip>
              )}
              {hasLastRun && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 font-medium"
                  data-testid="filter-last-run-badge"
                  title={`Kept ${lastRun.kept_count} of ${lastRun.input_count} elements`}
                >
                  kept {lastRun.kept_count} / {lastRun.input_count}
                </span>
              )}
            </div>
          )}
        </div>

        {data.status === 'running' && (
          <div className="absolute inset-0 rounded-lg border-2 border-blue-500 animate-pulse" />
        )}

        {/* AC-1464: bidirectional handles on every cardinal direction so
            selectEdgeHandles (layoutEngine.ts) can bind incoming AND outgoing
            edges regardless of which side the connected node sits on. The
            previous declaration only had target on top/left and source on
            right/bottom -- when the auto-layout placed downstream nodes to
            the left (e.g. filter -> run_agent going left) the edge picked
            sourceHandle="left" but no source-typed left handle existed, and
            React Flow silently dropped the edge. AgentNode and RDAPLookupNode
            (AC-1455) already follow this pattern. */}
        <Handle type="target" position={Position.Top} id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Top} id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="target" position={Position.Left} id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Left} id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="target" position={Position.Right} id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Right} id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="target" position={Position.Bottom} id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
        <Handle type="source" position={Position.Bottom} id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white" />
      </div>
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Filter Step"
          message="Are you sure you want to delete this filter step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default FilterNode;
